import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertImpactOversightRatingMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertImpactOversightRatingDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export type InsertImpactOversightRatingInput = {
  OversightAssessmentId: string;
  RatedItemId: string;
  Ratings: { ImpactId: string; Rating: number }[];
  TestDate: string;
  Likelihood?: number | null;
  CompletedBy?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useInsertImpactOversightRating = createMutationHook({
  name: 'insertImpactOversightRating',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.assessment.insertImpactOversightRating.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (input: InsertImpactOversightRatingInput) => input,
  invalidate: (trpc) => [
    // Apollo parity: evictField(cache, 'impact_oversight_rating') — the
    // compound + register + by-id reads that surface the new rows.
    trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
    trpc.frontend.assessment.oversightResultById.pathKey(),
    trpc.frontend.impactRating.oversightImpactRatingById.pathKey(),
    // Aggregate parity: evictField(cache, 'impact_oversight_rating') count.
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    // Apollo parity: evictField(cache, 'oversight_assessment')
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
    trpc.frontend.assessment.oversightActivityCount.queryKey(),
  ],
  mapResponse: (result: {
    Ids: string[];
  }): InsertImpactOversightRatingMutation => ({
    insertChildImpactOversightRating: {
      __typename: 'insertChildImpactRatingOutput',
      Ids: result.Ids,
    },
  }),
  graphqlDocument: InsertImpactOversightRatingDocument,
  graphqlVariables: (input: InsertImpactOversightRatingInput) => input,
  evictFields: ['impact_oversight_rating', 'oversight_assessment'],
});
